import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Stack, TextField, MenuItem, Button, Pagination, Chip,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Navebarehero } from '../Components/Navebarehero'
import { projectsData, getUniqueDevelopers, getUniqueLocations } from '../Data/projectsData'
import vedio from '../Images/hero vedio.mp4'

const PAGE_SIZE = 6

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#D9D6CF',
    borderRadius: '12px',
    bgcolor: 'rgba(217,214,207,0.06)',
    '& fieldset': { borderColor: 'rgba(217,214,207,0.25)' },
    '&:hover fieldset': { borderColor: 'rgba(217,214,207,0.45)' },
    '&.Mui-focused fieldset': { borderColor: '#7A2E3A' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(217,214,207,0.65)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#7A2E3A' },
}

const ProjectsPageHero = ({ isArabic, filterBar, title, subtitle }) => (
  <Box sx={{ position: 'relative', width: '100%', height: { xs: '75vh', md: '85vh' }, bgcolor: '#2B2B2B' }}>
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
      >
        <source src={vedio} type="video/mp4" />
      </Box>

      {/* overlay أساسي لتحسين تباين النص */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background: 'linear-gradient(180deg, rgba(43,43,43,0.6) 0%, rgba(122,46,58,0.15) 50%, rgba(43,43,43,0.55) 100%)',
        }}
      />

      {/* ✨ gradient في آخر جزء من الفيديو فقط ليمتزج مع خلفية الصفحة */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '35%',
          background:
            'linear-gradient(180deg, rgba(43,43,43,0) 0%, rgba(43,43,43,0.5) 40%, rgba(43,43,43,0.9) 80%, #2B2B2B 100%)',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />

      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 1.5,
          px: 2,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
            fontWeight: isArabic ? 700 : 600,
            fontSize: { xs: '2.3rem', md: '3.8rem' },
            color: '#fff',
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ color: 'rgba(217,214,207,0.8)', maxWidth: 480 }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        transform: 'translateY(50%)',
        zIndex: 5,
        px: { xs: 2, md: 6 },
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1300 }}>{filterBar}</Box>
    </Box>
  </Box>
)

export const AllProjects = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  const lang = isArabic ? 'ar' : 'en'

  const [search, setSearch] = useState('')
  const [developer, setDeveloper] = useState('all')
  const [location, setLocation] = useState('all')
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [page, setPage] = useState(1)

  const developers = useMemo(() => getUniqueDevelopers(), [])
  const locations = useMemo(() => getUniqueLocations(lang), [lang])

  const filtered = useMemo(() => {
    return projectsData.filter((project) => {
      const matchesSearch = project.name[lang].toLowerCase().includes(search.toLowerCase())
      const matchesDeveloper = developer === 'all' || project.developer === developer
      const matchesLocation = location === 'all' || project.location[lang] === location
      const matchesMin = minPrice === '' || project.price >= Number(minPrice)
      const matchesMax = maxPrice === '' || project.price <= Number(maxPrice)
      return matchesSearch && matchesDeveloper && matchesLocation && matchesMin && matchesMax
    })
  }, [search, developer, location, minPrice, maxPrice, lang])

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value)
    setPage(1)
  }

  const handleReset = () => {
    setSearch('')
    setDeveloper('all')
    setLocation('all')
    setMinPrice('')
    setMaxPrice('')
    setPage(1)
  }

  const filterBar = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: '1.4fr 1fr 1fr 0.8fr 0.8fr',
        },
        gap: 2,
        border: '1px solid rgba(217,214,207,0.2)',
        borderRadius: '20px',
        bgcolor: 'rgba(43,43,43,0.85)',
        backdropFilter: 'blur(16px) saturate(160%)',
        WebkitBackdropFilter: 'blur(16px) saturate(160%)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
        p: { xs: 2.5, md: 3.5 },
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={t('allProjects.searchPlaceholder')}
        value={search}
        onChange={handleFilterChange(setSearch)}
        sx={fieldSx}
      />

      <TextField
        select
        fullWidth
        size="small"
        label={t('allProjects.developer')}
        value={developer}
        onChange={handleFilterChange(setDeveloper)}
        sx={fieldSx}
      >
        <MenuItem value="all">{t('allProjects.allDevelopers')}</MenuItem>
        {developers.map((dev) => (
          <MenuItem key={dev} value={dev}>{dev}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        fullWidth
        size="small"
        label={t('allProjects.location')}
        value={location}
        onChange={handleFilterChange(setLocation)}
        sx={fieldSx}
      >
        <MenuItem value="all">{t('allProjects.allLocations')}</MenuItem>
        {locations.map((loc) => (
          <MenuItem key={loc} value={loc}>{loc}</MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        size="small"
        type="number"
        label={t('allProjects.minPrice')}
        value={minPrice}
        onChange={handleFilterChange(setMinPrice)}
        sx={fieldSx}
      />

      <TextField
        fullWidth
        size="small"
        type="number"
        label={t('allProjects.maxPrice')}
        value={maxPrice}
        onChange={handleFilterChange(setMaxPrice)}
        sx={fieldSx}
      />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ gridColumn: '1 / -1', mt: 1 }}
      >
        <Typography sx={{ color: 'rgba(217,214,207,0.75)', fontSize: '0.85rem' }}>
          {filtered.length} {t('allProjects.resultsFound')}
        </Typography>
        <Button
          onClick={handleReset}
          startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
          size="small"
          sx={{ color: 'rgba(217,214,207,0.85)', '&:hover': { color: '#7A2E3A' } }}
        >
          {t('allProjects.reset')}
        </Button>
      </Stack>
    </Box>
  )

  return (
    <Box sx={{ bgcolor: '#2B2B2B', minHeight: '100vh' }}>
      <Navebarehero />
      <ProjectsPageHero
        isArabic={isArabic}
        filterBar={filterBar}
        title={t('allProjects.title')}
        subtitle={t('allProjects.subtitle')}
      />

      <Box
        sx={{
          maxWidth: 1300,
          mx: 'auto',
          px: { xs: 3, md: 6 },
          pt: { xs: 16, md: 14 },
          pb: { xs: 10, md: 14 },
          minHeight: '60vh',
        }}
      >
        {paginated.length > 0 ? (
          <AnimatePresence mode="wait">
            <Box
              key={page + search + developer + location + minPrice + maxPrice}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 3,
              }}
            >
              {paginated.map((project, index) => (
                <Box
                  key={project.id}
                  component={motion.div}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  sx={{
                    position: 'relative',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    border: '1px solid rgba(217,214,207,0.12)',
                    bgcolor: 'rgba(217,214,207,0.02)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'border-color 0.3s ease',
                    '&:hover': { borderColor: 'rgba(122,46,58,0.5)' },
                  }}
                >
                  {/* الصورة */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: '4 / 3',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={project.image}
                      alt={project.name[lang]}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />

                    <Chip
                      label={project.developer}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        [isArabic ? 'right' : 'left']: 12,
                        bgcolor: 'rgba(43,43,43,0.75)',
                        backdropFilter: 'blur(6px)',
                        color: '#D9D6CF',
                        fontSize: '0.75rem',
                        zIndex: 3,
                      }}
                    />
                  </Box>

                  {/* ✨ gradient على مستوى الكارت كله - يبدأ من فوق منطقة التفاصيل ويمتد جوه الصورة */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(180deg, rgba(43,43,43,0) 0%, rgba(43,43,43,0) 55%, rgba(43,43,43,0.4) 72%, rgba(43,43,43,0.85) 88%, rgba(217,214,207,0.02) 100%)',
                      pointerEvents: 'none',
                      zIndex: 2,
                    }}
                  />

                  {/* التفاصيل */}
                  <Stack spacing={1} sx={{ p: 3, flexGrow: 1, position: 'relative', zIndex: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <LocationOnIcon sx={{ fontSize: 15, color: '#7A2E3A' }} />
                      <Typography sx={{ color: 'rgba(217,214,207,0.7)', fontSize: '0.8rem' }}>
                        {project.location[lang]}
                      </Typography>
                    </Stack>

                    <Typography
                      sx={{
                        fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
                        fontWeight: 600,
                        fontSize: '1.15rem',
                        color: '#D9D6CF',
                      }}
                    >
                      {project.name[lang]}
                    </Typography>

                    <Typography sx={{ color: '#FFFFFF', fontSize: '0.95rem', fontWeight: 600 }}>
                      {project.price.toLocaleString()} {isArabic ? 'درهم' : 'AED'}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    <Button
                      fullWidth
                      onClick={() => navigate(`/projects/${project.id}`)}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16, transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
                      sx={{
                        justifyContent: 'space-between',
                        mt: 1.5,
                        py: 1,
                        borderRadius: '999px',
                        border: '1px solid rgba(217,214,207,0.25)',
                        color: '#D9D6CF',
                        '&:hover': { borderColor: '#7A2E3A', bgcolor: 'rgba(122,46,58,0.08)' },
                      }}
                    >
                      {t('allProjects.viewMore')}
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Box>
          </AnimatePresence>
        ) : (
          <Typography sx={{ color: 'rgba(217,214,207,0.5)', textAlign: 'center', py: 10 }}>
            {t('allProjects.noResults')}
          </Typography>
        )}

        {/* الباجينيشن */}
        {totalPages > 1 && (
          <Stack alignItems="center" sx={{ mt: 6 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              dir={isArabic ? 'rtl' : 'ltr'}
              sx={{
                '& .MuiPaginationItem-root': { color: 'rgba(217,214,207,0.7)', borderColor: 'rgba(217,214,207,0.2)' },
                '& .Mui-selected': { bgcolor: '#7A2E3A !important', color: '#D9D6CF' },
              }}
            />
          </Stack>
        )}
      </Box>
    </Box>
  )
}